import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Divider,
  Breadcrumbs,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const PrivacyPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link component={RouterLink} to="/" color="inherit">
          Home
        </Link>
        <Typography color="text.primary">Privacy Policy</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Last Updated: March 7, 2025
        </Typography>
      </Box>
      
      {/* Content */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          borderRadius: 4
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          1. Introduction
        </Typography>
        <Typography variant="body1" paragraph>
          At SharkPanzer ("we," "our," or "us"), we value your privacy and are committed to protecting your personal information. SharkPanzer is a product of UnwiringTech®. Unwiring Tech GSTIN- 09CYNPA1063Q1ZZ. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile application, or any other services we offer (collectively, the "Services").
        </Typography>
        <Typography variant="body1" paragraph>
          Please read this Privacy Policy carefully. By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, we recommend you refrain from using our Services.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          2. Information We Collect
        </Typography>
        <Typography variant="body1" paragraph>
          We collect several types of information from and about users of our Services, including:
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          2.1 Personal Information
        </Typography>
        <Typography variant="body1" paragraph>
          Personal information is information that identifies you as an individual. We may collect the following personal information:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Contact information (such as name, email address, and phone number)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Account credentials (such as username and password)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Payment information (such as credit card details, billing address, and transaction history)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Profile information (such as profile picture and preferences)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Communications with us (such as customer support inquiries and feedback)
            </Typography>
          </li>
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          2.2 Usage Information
        </Typography>
        <Typography variant="body1" paragraph>
          We may also collect information about how you use our Services, including:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Log data (such as IP address, browser type, operating system, and pages visited)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Device information (such as device type, unique device identifier, and mobile network information)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Usage patterns (such as features used, time spent on the Services, and frequency of use)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              YouTube videos processed through our Services
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Content generated by our Services based on your inputs
            </Typography>
          </li>
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          2.3 Cookies and Similar Technologies
        </Typography>
        <Typography variant="body1" paragraph>
          We use cookies and similar tracking technologies to track activity on our Services and to hold certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device.
        </Typography>
        <Typography variant="body1" paragraph>
          You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you choose to disable cookies, certain features of our Services may not function as intended.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          3. How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use the information we collect for various purposes, including to:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Provide, maintain, and improve our Services
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Process and complete transactions, and send related information including confirmations and invoices
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Respond to your comments, questions, and requests, and provide customer service
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Send technical notices, updates, security alerts, and support and administrative messages
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Communicate with you about products, services, offers, promotions, and events, and provide other news or information about us and our partners
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Monitor and analyze trends, usage, and activities in connection with our Services
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Detect, investigate, and prevent fraudulent transactions and other illegal activities and protect the rights and property of SharkPanzer and others
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Personalize and improve the Services, and provide content, features, or advertisements that match your interests and preferences
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Facilitate contests, sweepstakes, and promotions and process and deliver entries and rewards
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Carry out any other purpose described to you at the time the information was collected
            </Typography>
          </li>
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          4. How We Share Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We may share your information in the following circumstances:
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          4.1 With Service Providers
        </Typography>
        <Typography variant="body1" paragraph>
          We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, hosting services, customer service, and marketing assistance.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          4.2 For Business Transfers
        </Typography>
        <Typography variant="body1" paragraph>
        If we are involved in a merger, acquisition, financing, reorganization, bankruptcy, receivership, sale of company assets, or transition of service to another provider, your information may be transferred as part of such a transaction in compliance with applicable laws and contractual obligations.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          4.3 For Legal Purposes
        </Typography>
        <Typography variant="body1" paragraph>
        We may share your information if required by law or in response to valid legal requests or subpoena or if we believe that such action is necessary to (a) comply with the law and the reasonable requests of law enforcement; (b) protect the security or integrity of our Services; and/or (c) exercise or protect the rights, property, or personal safety of SharkPanzer, our users, or others.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          4.4 With Your Consent
        </Typography>
        <Typography variant="body1" paragraph>
          We may share your information with your consent or at your direction.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          4.5 Aggregated or De-identified Information
        </Typography>
        <Typography variant="body1" paragraph>
          We may share aggregated or de-identified information, which cannot reasonably be used to identify you, with third parties for research, marketing, analytics, and other purposes.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          5. Data Security
        </Typography>
        <Typography variant="body1" paragraph>
          We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, please also remember that we cannot guarantee that the internet itself is 100% secure. While we strive to ensure secure transmission of your data, please be aware that online data transfer carries inherent risks. You should only access the Services within a secure environment.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          6. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          7. Your Rights and Choices
        </Typography>
        <Typography variant="body1" paragraph>
          Depending on your location, you may have certain rights regarding your personal information, including:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              The right to access, update, or delete the information we have on you
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              The right of rectification - the right to have your information corrected if it is inaccurate or incomplete
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              The right to object to our processing of your personal information
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              The right of restriction - the right to request that we restrict the processing of your personal information
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              The right to data portability - the right to be provided with a copy of your personal information in a structured, machine-readable, and commonly used format
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              The right to withdraw consent - the right to withdraw your consent at any time where we relied on your consent to process your personal information
            </Typography>
          </li>
        </Typography>
        <Typography variant="body1" paragraph>
          To exercise these rights, please contact us at support@unwiringtech.com.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          8. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          Our Services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us. If we become aware that we have collected personal information from children without verification of parental consent, we will take steps to remove that information from our servers.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          9. International Data Transfers
        </Typography>
        <Typography variant="body1" paragraph>
          Your information may be transferred to, and maintained on, computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those in your jurisdiction. If you are located outside India and choose to provide information to us, please note that we transfer the information, including personal information, to India and process it there.
        </Typography>
        <Typography variant="body1" paragraph>
        By using our Services and providing your information, you consent to the transfer and processing of your data in accordance with this Privacy Policy.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          10. Third-Party Links and Services
        </Typography>
        <Typography variant="body1" paragraph>
          Our Services may contain links to third-party websites and services. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You should review the privacy policies of these third-party sites before providing any information to them.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          11. Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy.
        </Typography>
        <Typography variant="body1" paragraph>
          You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          12. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about this Privacy Policy, please contact us at:
        </Typography>
        <Typography variant="body1" paragraph>
          Email: support@unwiringtech.com
        </Typography>
      </Paper>
    </Container>
  );
};

export default PrivacyPage;
